import { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Chip, Avatar, IconButton, InputBase, CircularProgress, ToggleButtonGroup, ToggleButton, TextField, Popover } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";
import api from "../services/Axios";

const ACCENT = "var(--primary-gradient, linear-gradient(135deg, #FF2D92, #A855F7))";
const POSITIVE = "#1DA463";

const cardStyle = {
    background: "#FFFFFF",
    border: "1px solid #EEF0F5",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(17,19,26,0.04)",
};

// ---- Build KPI + funnel data from the live API response ----
function buildKpiCards(data) {
    if (!data) return [];
    const landing = data.rows?.[0] || {};
    return [
        { label: "Page visits", value: landing.sessions ?? 0 },
        { label: "Active users", value: landing.active_users ?? 0 },
        { label: "New users", value: landing.new_users ?? 0 },
        { label: "Returning users", value: landing.returning_users ?? 0 },
        { label: "Registered users", value: data.signed_up ?? 0 },
        { label: "Subscribers", value: data.subscribed ?? 0 },
        { label: "Users who messaged", value: data.sent_msg ?? 0 },
        { label: "Voice callers", value: data.tried_voice ?? 0 },
        { label: "Video callers", value: data.tried_video ?? 0 },
    ];
}

function buildFunnelSteps(data) {
    if (!data) return [];
    const landing = data.rows?.[0] || {};
    return [
        { label: `Visited ${landing.landing_page || "landing"} page`, value: landing.sessions ?? 0 },
        { label: `Active users`, value: landing.active_users ?? 0 },
        { label: `New users`, value: landing.new_users ?? 0 },
        { label: `Returning users`, value: landing.returning_users ?? 0 },
        { label: "Registered", value: data.signed_up ?? 0 },
        { label: "Subscribed", value: data.subscribed ?? 0 },
        { label: "Sent a message", value: data.sent_msg ?? 0 },
        { label: "Made a voice call", value: data.tried_voice ?? 0 },
        { label: "Made a video call", value: data.tried_video ?? 0 },
    ];
}
function getDateRange(period, customStart, customEnd) {
    if (period === "custom") {
        return {
            start_date: customStart || "30daysAgo",
            end_date: customEnd || "today",
        };
    }
    const map = {
        today: { start_date: "today", end_date: "today" },
        "7d": { start_date: "7daysAgo", end_date: "today" },
        week: { start_date: "7daysAgo", end_date: "today" },
        month: { start_date: "30daysAgo", end_date: "today" },
        year: { start_date: "365daysAgo", end_date: "today" },
    };
    return map[period] || map.month;
}
const PERIOD_OPTIONS = [
    { value: "today", label: "Today" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
    { value: "year", label: "Yearly" },
    { value: "custom", label: "Custom" },
];

function PeriodFilter({ period, onPeriodChange, customStart, customEnd, onCustomChange }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handlePeriodClick = (e, val) => {
        if (!val) return;
        if (val === "custom") {
            setAnchorEl(e.currentTarget);
        }
        onPeriodChange(val);
    };

    return (
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <ToggleButtonGroup
                exclusive
                value={period}
                onChange={handlePeriodClick}
                sx={{
                    background: "#F0F1F5",
                    borderRadius: "10px",
                    p: 0.5,
                    "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "8px !important",
                        px: 1.8,
                        py: 0.6,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#666",
                        textTransform: "none",
                        "&.Mui-selected": {
                            background: "#fff",
                            color: "#111",
                            boxShadow: "0 1px 3px rgba(17,19,26,0.08)",
                        },
                    },
                }}
            >
                {PERIOD_OPTIONS.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}>
                        {opt.value === "custom" && <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.6 }} />}
                        {opt.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {period === "custom" && customStart && customEnd && (
                <Typography fontSize={13} color="#888">
                    {customStart} → {customEnd}
                </Typography>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box p={2} display="flex" gap={1.5} alignItems="center">
                    <TextField
                        type="date"
                        label="Start"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={customStart}
                        onChange={(e) => onCustomChange(e.target.value, customEnd)}
                    />
                    <TextField
                        type="date"
                        label="End"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={customEnd}
                        onChange={(e) => onCustomChange(customStart, e.target.value)}
                    />
                </Box>
            </Popover>
        </Box>
    );
}
// ---- Framer Motion modal (replaces MUI Dialog) ----
function CreatorPickerModal({ open, onClose, onSelect, creators, loading }) {
    const [search, setSearch] = useState("");

    const filtered = creators.filter((c) =>
        (c.name || c.username || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    onClick={onClose}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(17,19,26,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1300,
                        padding: 16,
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 12 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            borderRadius: 16,
                            border: "1px solid #EEF0F5",
                            boxShadow: "0 8px 32px rgba(17,19,26,0.12)",
                            width: "100%",
                            maxWidth: 400,
                            maxHeight: "80vh",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={2.5} py={2}>
                            <Typography fontWeight={700} fontSize={16} color="#111">
                                Select creator
                            </Typography>
                            <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box px={2.5} pb={1.5}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: "10px",
                                    background: "#F7F8FA",
                                    border: "1px solid #EEF0F5",
                                }}
                            >
                                <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                                <InputBase
                                    placeholder="Search creators..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{ color: "#111", flex: 1, fontSize: 14 }}
                                    autoFocus
                                />
                            </Box>
                        </Box>

                        <Box px={1.5} pb={2} sx={{ overflowY: "auto" }}>
                            {loading ? (
                                <Box display="flex" justifyContent="center" py={3}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : filtered.length === 0 ? (
                                <Typography align="center" sx={{ color: "#999", py: 3 }} variant="body2">
                                    No creators found
                                </Typography>
                            ) : (
                                filtered.map((creator) => (
                                    <Box
                                        key={creator.id || creator._id || creator.username}
                                        onClick={() => {
                                            onSelect(creator);
                                            onClose();
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            "&:hover": { background: "#F5F3FE" },
                                        }}
                                    >
                                        <Avatar src={creator.avatar_url || creator.photo_url} sx={{ width: 36, height: 36 }}>
                                            {(creator.name || creator.username || "?")[0]?.toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography fontSize={14} color="#111">
                                                {creator.name || creator.username}
                                            </Typography>
                                            {creator.username && creator.name && (
                                                <Typography fontSize={12} color="#999">
                                                    @{creator.username}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function CreatorAnalytics() {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [creators, setCreators] = useState([]);
    const [creatorsLoading, setCreatorsLoading] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [period, setPeriod] = useState("month");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    const getUserEngagementData = useCallback(async () => {
        if (!selectedCreator?.username) return;
        setAnalyticsLoading(true);
        try {
            const { start_date, end_date } = getDateRange(period, customStart, customEnd);
            const res = await api.get("/admin/analytics/ga/user-engagement-analysis", {
                params: { username: selectedCreator.username, start_date, end_date },
            });
            setAnalytics(res?.data || null);
        } catch (error) {
            console.error("Error fetching user engagement data:", error);
            setAnalytics(null);
        } finally {
            setAnalyticsLoading(false);
        }
    }, [selectedCreator, period, customStart, customEnd]);

    const getAllCreators = async () => {
        setCreatorsLoading(true);
        try {
            const res = await api.get("/admin/creators/list");
            const list = res?.data?.creators || res?.data || [];
            setCreators(list);
            setSelectedCreator(list[0] || null);
        } finally {
            setCreatorsLoading(false);
        }
    };

    useEffect(() => {
        getUserEngagementData();
    }, [getUserEngagementData]);

    useEffect(() => {
        getAllCreators();
    }, []);

    const kpiCards = buildKpiCards(analytics);
    const funnelSteps = buildFunnelSteps(analytics);
    const maxFunnelValue = Math.max(...funnelSteps.map((s) => s.value), 1);

    return (
        <Box sx={{ background: "#F7F8FA", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Box
                    onClick={() => setModalOpen(true)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        cursor: "pointer",
                        background: "#fff",
                        border: "1px solid #EEF0F5",
                        borderRadius: "14px",
                        px: 1.5,
                        py: 1,
                        transition: "all 0.15s ease",
                        "&:hover": {
                            borderColor: "#D9DCE5",
                            boxShadow: "0 2px 8px rgba(17,19,26,0.06)",
                        },
                    }}
                >
                    <Avatar
                        src={selectedCreator?.avatar_url || selectedCreator?.photo_url}
                        sx={{ background: ACCENT, width: 40, height: 40, fontWeight: 700, fontSize: 16 }}
                    >
                        {selectedCreator?.first_name?.[0]?.toUpperCase() || selectedCreator?.name?.[0]?.toUpperCase() || "?"}
                    </Avatar>

                    <Box>
                        <Typography color="#9AA0AC" fontSize={11} fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.03em", mb: 0.2 }}>
                            Viewing analytics for
                        </Typography>
                        <Typography color="#111" fontWeight={700} fontSize={16}>
                            {selectedCreator?.first_name
                                ? `${selectedCreator.first_name} ${selectedCreator.last_name || ""}`.trim()
                                : selectedCreator?.name || analytics?.username || "Select a creator"}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                            ml: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: "8px",
                            background: "#F7F8FA",
                        }}
                    >
                        <Typography fontSize={12} fontWeight={600} color="#666">
                            Change Creator
                        </Typography>
                        <ChevronRightIcon fontSize="small" sx={{ color: "#999", fontSize: 16 }} />
                    </Box>
                </Box>

                <Chip
                    label="Live dashboard"
                    size="small"
                    sx={{ background: "#E6F7EE", color: POSITIVE, fontWeight: 600, border: "1px solid #C9EFDA" }}
                />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1.5}>
                <Typography color="#111" fontWeight={700} fontSize={16}>
                    Overview
                </Typography>
                <PeriodFilter
                    period={period}
                    onPeriodChange={setPeriod}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomChange={(s, e) => {
                        setCustomStart(s);
                        setCustomEnd(e);
                    }}
                />
            </Box>

            {analyticsLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    {/* KPI Cards */}
                    <Grid container spacing={2} mb={4}>
                        {kpiCards.map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.label}>
                                <Box sx={cardStyle} p={2.5}>
                                    <Typography color="#888" fontSize={13} mb={1}>
                                        {card.label}
                                    </Typography>
                                    <Typography color="#111" fontWeight={700} fontSize={28}>
                                        {card.value.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Conversion Funnel */}
                    <Box sx={cardStyle} p={3}>
                        <Typography color="#111" fontWeight={700} fontSize={16} mb={2.5}>
                            User conversion funnel
                        </Typography>

                        <Box display="flex" flexDirection="column" gap={2.5}>
                            {funnelSteps.map((step) => (
                                <Box key={step.label}>
                                    <Box display="flex" justifyContent="space-between" mb={0.8}>
                                        <Typography color="#444" fontSize={14}>
                                            {step.label}
                                        </Typography>
                                        <Typography color="#111" fontWeight={700} fontSize={14}>
                                            {step.value.toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: 8, borderRadius: 999, background: "#F0F1F5", overflow: "hidden" }}>
                                        <Box
                                            sx={{
                                                height: "100%",
                                                borderRadius: 999,
                                                width: `${(step.value / maxFunnelValue) * 100}%`,
                                                background: ACCENT,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            <CreatorPickerModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSelect={setSelectedCreator}
                creators={creators}
                loading={creatorsLoading}
            />
        </Box>
    );
}